import type { BrowseTrack } from "@touchscreen/api-client";
import { artworkUrl } from "../lib/artwork.js";
import { LikeButton } from "./LikeButton.js";
import { Artwork } from "@touchscreen/brand-kit";
import "./ConfirmRequestSheet.css";

export interface ConfirmRequestSheetProps {
  track: BrowseTrack;
  submitting: boolean;
  disabledReason?: string | null;
  errorMessage?: string | null;
  /** When set, the primary button becomes this instead of "Request" — a clean swap rather than
   * an error state, used when this venue needs location sharing re-proven before a request can
   * go through (geofence pre-check hasn't passed yet). */
  primaryAction?: { label: string; onClick: () => void } | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmRequestSheet(props: ConfirmRequestSheetProps): React.JSX.Element {
  const { track, submitting, disabledReason, errorMessage, primaryAction, onConfirm, onCancel } = props;
  return (
    <div className="confirm-sheet" role="dialog" aria-modal="true">
      <div className="confirm-sheet__backdrop" onClick={onCancel} />
      <div className="confirm-sheet__panel">
        <p className="confirm-sheet__eyebrow">Request this track?</p>
        <Artwork className="confirm-sheet__artwork" src={artworkUrl(track.sm_id)} />
        <div className="confirm-sheet__heading">
          <div>
            <h2 className="confirm-sheet__title">{track.title}</h2>
            <h3 className="confirm-sheet__artist">{track.artist}</h3>
          </div>
          <LikeButton
            smId={track.sm_id}
            title={track.title}
            artist={track.artist}
            className="confirm-sheet__like"
          />
        </div>
        {disabledReason ? <p className="confirm-sheet__reason">{disabledReason}</p> : null}
        {!disabledReason && errorMessage ? <p className="confirm-sheet__reason">{errorMessage}</p> : null}
        <button
          className="confirm-sheet__confirm"
          onClick={primaryAction ? primaryAction.onClick : onConfirm}
          disabled={!primaryAction && (submitting || Boolean(disabledReason))}
        >
          {primaryAction ? primaryAction.label : submitting ? "Requesting…" : "Request"}
        </button>
        <button className="confirm-sheet__cancel" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
      </div>
    </div>
  );
}
